import { AddressDtoSchema } from "@modules/address/addressDto"
import { Box, BoxDraft, BoxTypeOf, ItemBox, PhoneNumberWithPrefixSchema } from "@modules/box/boxTypes"
import { BoxStaffRole } from "@modules/boxStaff/gateway/boxStaffDto"
import { convertImageFromDto, HUImageDtoSchema } from "@modules/dtos/imageDto"
import { StripeAccountErrorCode } from "@modules/finances/types/stripeErrorTypes"
import { StripeAccountStatuEnum } from "@modules/payment/stripeTypes"
import { convertUserFromDto, UserDtoSchema } from "@modules/profile/profileDtos"
import { LocationDtoSchema } from "@modules/types/locationTypes"
import { convertToEnum } from "@modules/utils/enum"
import { z } from "zod"

export const ItemBoxDtoSchema = z.object({
  id: z.string(),
  logo: HUImageDtoSchema,
  cover: HUImageDtoSchema,
  name: z.string(),
  address: AddressDtoSchema,
  phoneNumberWithPrefix: PhoneNumberWithPrefixSchema,
  email: z.string(),
})

export type ItemBoxDto = z.infer<typeof ItemBoxDtoSchema>

export const BoxDtoSchema = z.object({
  id: z.string(),
  name: z.string(),
  address: AddressDtoSchema,
  phoneNumberWithPrefix: PhoneNumberWithPrefixSchema,
  email: z.string(),
  owner: UserDtoSchema,
  logo: HUImageDtoSchema,
  cover: HUImageDtoSchema,
  photos: z.array(HUImageDtoSchema),
  siren: z.string(),
  description: z.string(),
  isSubscribed: z.boolean(),
  stripeAccountStatus: z.nativeEnum(StripeAccountStatuEnum).nullable(),
  stripeAccountCurrency: z.string().nullable(),
  stripeMinimumAmount: z.number().nullable(),
  stripeMaximumAmount: z.number().nullable(),
  accountError: z.nativeEnum(StripeAccountErrorCode).nullable(),
  currentUserRole: z.string().nullable(),
  location: LocationDtoSchema.nullable(),
  slotGeneratorInWeeks: z.number().nullable(),
  zoneId: z.string().nullable(),
})

export type BoxDto = z.infer<typeof BoxDtoSchema>

export const BoxDraftDtoSchema = z.object({
  name: z.string(),
  address: AddressDtoSchema,
  phoneNumberWithPrefix: PhoneNumberWithPrefixSchema,
  email: z.string(),
  logo: z.string().nullable(),
  cover: z.string().nullable(),
  photos: z.array(z.string()),
  siren: z.string(),
  description: z.string(),
  location: LocationDtoSchema.nullable(),
  slotGeneratorInWeeks: z.number(),
  zoneId: z.string().nullable(),
})

export type BoxDraftDto = z.infer<typeof BoxDraftDtoSchema>

export const InfosSubscriptionDtoSchema = z.object({
  subscriptionDate: z.string(),
  minimumCommitmentPeriod: z.boolean(),
  subscriptionEndDate: z.string(),
})

export type InfosSubscriptionDto = z.infer<typeof InfosSubscriptionDtoSchema>

export function convertItemBoxFromDto(dto: ItemBoxDto): ItemBox {
  return {
    ...dto,
    _type: BoxTypeOf,
    logo: convertImageFromDto(dto.logo),
    cover: convertImageFromDto(dto.cover),
  }
}

export function convertBoxFromDto(dto: BoxDto): Box {
  return {
    ...dto,
    _type: BoxTypeOf,
    author: convertUserFromDto(dto.owner),
    logo: convertImageFromDto(dto.logo),
    cover: convertImageFromDto(dto.cover),
    boxPhotos: dto.photos ? dto.photos.map((image) => convertImageFromDto(image)) : [],
    currentUserRole: convertToEnum(BoxStaffRole, dto.currentUserRole),
    stripeAccountStatus: dto.stripeAccountStatus
      ? convertToEnum(StripeAccountStatuEnum, dto.stripeAccountStatus)
      : undefined,
    stripeAccountCurrency: dto.stripeAccountCurrency ?? undefined,
    stripeMaximumAmount: dto.stripeMaximumAmount ?? undefined,
    stripeMinimumAmount: dto.stripeMinimumAmount ?? undefined,
    accountError: dto.accountError ?? undefined,
    location: dto.location ?? undefined,
    slotGeneratorInWeeks: dto.slotGeneratorInWeeks ?? undefined,
    zoneId: dto.zoneId ?? null,
  }
}

export function convertBoxDraftToDto(draft: BoxDraft): BoxDraftDto {
  return {
    name: draft.name,
    address: draft.address,
    phoneNumberWithPrefix: draft.phoneNumberWithPrefix,
    email: draft.email,
    logo: draft.logo?.path ?? null,
    cover: draft.cover?.path ?? null,
    photos: draft.boxPhotos.map((image) => image.path),
    siren: draft.siren,
    description: draft.description,
    location: draft.location ?? null,
    slotGeneratorInWeeks: draft.slotGeneratorInWeeks ?? 2,
    zoneId: draft.zoneId ?? null,
  }
}

export function convertPartialBoxDraftToDto(draft: Partial<BoxDraft>): Partial<BoxDraftDto> {
  return {
    name: draft.name ?? undefined,
    address: draft.address ?? undefined,
    phoneNumberWithPrefix: draft.phoneNumberWithPrefix ?? undefined,
    email: draft.email ?? undefined,
    siren: draft.siren ?? undefined,
    description: draft.description ?? undefined,
    logo: draft.logo?.path,
    cover: draft.cover?.path,
  }
}
